/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: =, Brian Bal
 */
import React, { useEffect, useMemo, useState } from "react"
import {
    Modal,
    Row,
    ModalTitle,
    TitleSection,
    ContentSection,
    Button,
    Icon,
    useToken,
    sharedMomentToCZ,
    FullGameDownloadNotAvailableContent,
} from "trux"
import {
    useFluidPlayer,
    useVideoSuspenders,
} from "trux/dist/organisms/VideoPlayer"
import moment from "moment"
import {
    calculateNoOfDaysBeforeExpiration,
    facebookShare,
    twitterShare,
} from "../../util/helpers"
import DownloadMomentWrapper from "../DownloadMomentWrapper"
import { FullVideoWarning } from "../FullVideoWarning/FullVideoWarning"
import "./ShareGameModal.css"
import { AutoAddMomentToTraceiDDialog } from "../AutoAddMomentToTraceiDDialog"

export const ShareGameModal = ({
    currentPlaylist,
    game,
    event,
    team,
    gameDate,
    extras,
    toast,
    history,
    user,
}) => {
    const token = useToken("ShareGameModal")

    let videoContainer = document.querySelector(".Game .VideoContainer")
    let videoContainerRef = { current: 24 }
    if (videoContainer) {
        let id = videoContainer.id
        id = id.replace("video-container-", "")
        id = parseInt(id, 10)
        videoContainerRef = { current: id }
    }

    const { suspend, resume } = useVideoSuspenders(videoContainerRef)
    useEffect(() => {
        suspend()
        return () => {
            resume()
        }
    }, [])

    let open = extras.share ? true : false
    let userId = user ? user.user_id : null
    let uploadTIme = game?.subtasks_statuses?.webapp_migration?.update_time
    let expirationDays =
        uploadTIme && calculateNoOfDaysBeforeExpiration(uploadTIme)
    let isFullVideoExpired =
        !expirationDays && currentPlaylist?.id === "full-game"
    let isFullVideoAvailable =
        expirationDays && currentPlaylist?.id === "full-game"

    let [step, setStep] = useState("main")

    let backLink = history.location.pathname.replace("/share", "")
    let downloadLink = history.location.pathname.replace(
        "/share",
        "/download-share"
    )

    const { insideMoment, getVideoInfo, monitor } =
        useFluidPlayer(videoContainerRef)
    let isFullGame = !insideMoment

    const fullGameTime = useMemo(() => {
        let info = getVideoInfo()
        if (info && info.scrubber) {
            return info.scrubber.currentTime
        } else {
            return 0
        }
    }, [getVideoInfo])

    const link = useMemo(() => {
        let url = window.location.href.replace("/share", "").replace("/#", "")
        if (isFullGame) {
            url += `/gt/${Math.floor(fullGameTime)}`
        }
        return url
    }, [isFullGame, fullGameTime])
    console.log("ShareGameModal link", link)

    const onClose = () => {
        history.push(backLink)
        let playEvent = new CustomEvent("trc-play")
        document.dispatchEvent(playEvent)
        setTimeout(() => {
            setStep("main")
        }, 1000)
    }

    const copyLink = (e) => {
        copy(e, toast)
        sharedMomentToCZ({
            type: "copy",
            user_id: userId,
            where: "main",
            moment_id: event?.game_clip_id,
        })
        setStep("auto-add")
    }

    const sendEmail = () => {
        sharedMomentToCZ({
            type: "email",
            user_id: userId,
            where: "main",
            moment_id: event?.game_clip_id,
        })
        setStep("auto-add")
    }

    const shareWithFacebook = (e) => {
        if (event) {
            setStep("auto-add")
            facebookShare(shareLink, userId, event.game_clip_id, e.target)
        }
    }

    const shareWithTwitter = (e) => {
        if (event) {
            setStep("auto-add")
            twitterShare(shareLink, userId, event.game_clip_id, e.target)
        }
    }

    let shareLink = link
    shareLink += "?mtm_campaign=gg"
    shareLink += "&mtm_kwd=copy"

    let emailShareLink = link
    emailShareLink += "?mtm_source=gg"
    emailShareLink += "&mtm_kwd=email"

    let mailToLink = gameEmailLink(emailShareLink, currentPlaylist, gameDate)

    let buttonWidth = 96

    const handleButtonOnClick = (e, type, link) => {
        switch (type) {
            case "download": {
                history.push(link)
                break
            }
            default: {
                break
            }
        }
    }

    return (
        <>
            {step !== "auto-add" && (
                <Modal
                    trkContext="Share Game"
                    open={open}
                    onShadeClick={onClose}
                    className="share-game-modal"
                >
                    {step !== "auto-add" && (
                        <TitleSection onCloseClick={onClose}>
                            <ModalTitle
                                content={
                                    step === "download" ? "Download" : "Sharing"
                                }
                            />
                        </TitleSection>
                    )}
                    {step === "download" &&
                        currentPlaylist?.id !== "full-game" && (
                            <DownloadMomentWrapper
                                onClose={() => setStep("main")}
                            />
                        )}
                    {step === "download" && isFullVideoAvailable && (
                        <DownloadMomentWrapper
                            onClose={() => setStep("main")}
                            expirationDays={expirationDays}
                        />
                    )}
                    {step === "download" && isFullVideoExpired && (
                        <FullGameDownloadNotAvailableContent
                            history={history}
                            pathToReplace={"share"}
                        />
                    )}
                    {step === "main" && (
                        <ContentSection>
                            <Row
                                center
                                alignItems="flex-start"
                                justifyContent="space-evenly"
                            >
                                <Button
                                    width={buttonWidth}
                                    trkAction="Facebook Pressed"
                                    trkLabel="Share Button"
                                    onClick={shareWithFacebook}
                                    icon={Icon.Type.Facebook}
                                    content="Facebook"
                                    marginBottom="6"
                                    marginLeft="3"
                                    marginRight="3"
                                />
                                <Button
                                    width={buttonWidth}
                                    trkAction="Twitter Pressed"
                                    trkLabel="Share Button"
                                    onClick={shareWithTwitter}
                                    icon={Icon.Type.Twitter}
                                    content="Twitter"
                                    marginBottom="6"
                                    marginLeft="3"
                                    marginRight="3"
                                />
                                <Button
                                    width={buttonWidth}
                                    trkAction="Copy Link Pressed"
                                    trkLabel="Share Button"
                                    onClick={(e) => copyLink(e)}
                                    icon={Icon.Type.Link}
                                    content="Copy link"
                                    marginBottom="6"
                                    marginLeft="3"
                                    marginRight="3"
                                />
                                <Button
                                    width={buttonWidth}
                                    trkAction="Email Pressed"
                                    trkLabel="Share Button"
                                    href={mailToLink}
                                    target="_blank"
                                    rel="noopener noreferer"
                                    onClick={() => sendEmail()}
                                    icon={Icon.Type.Email}
                                    content="Email"
                                    marginLeft="3"
                                    marginRight="3"
                                />
                                <textarea
                                    readOnly
                                    id="copy-link"
                                    style={{
                                        height: "1px",
                                        width: "1px",
                                        position: "absolute",
                                        top: "-999px",
                                        left: "-999px",
                                    }}
                                    value={shareLink}
                                />
                                <Button
                                    width={buttonWidth}
                                    trkAction="Download Pressed"
                                    onClick={(e) =>
                                        handleButtonOnClick(
                                            e,
                                            "download",
                                            downloadLink
                                        )
                                    }
                                    icon={Icon.Type.Download}
                                    content="Download"
                                    marginLeft="3"
                                    marginRight="3"
                                />
                                {canNativeShare() && (
                                    <Button
                                        trkAction="Share To Pressed"
                                        trkLabel="Share Button"
                                        width={buttonWidth}
                                        onClick={() => {
                                            nativeShareMoment(
                                                game,
                                                currentPlaylist,
                                                event,
                                                user
                                            ).then(() => {
                                                setStep("auto-add")
                                            })
                                        }}
                                        icon={Icon.Type.Action}
                                        content="Share to"
                                        marginBottom="2"
                                        marginLeft="3"
                                        marginRight="3"
                                    />
                                )}
                            </Row>
                            {isFullVideoAvailable && (
                                <FullVideoWarning
                                    marginTop="8"
                                    expirationDays={expirationDays}
                                />
                            )}
                        </ContentSection>
                    )}
                </Modal>
            )}
            {step === "auto-add" && (
                <AutoAddMomentToTraceiDDialog
                    open={true}
                    team={team}
                    token={token}
                    moment={event}
                    title={"Link copied"}
                    onClose={() => history.push(backLink)}
                    disableAutoAdd={event?.type === "FullGameVideo"}
                />
            )}
        </>
    )
}

function gameEmailLink(url, playlist, date) {
    let subject = `${playlist.title} Playlist`

    let body = `Hi, check out this moment from the ${playlist.title} playlist on ${date}`
    body += "\n"
    body += url
    body += "\n"
    body += "\n"
    body += `You can also watch ${playlist.event_count} other moments in this playlist`
    body += "\n"
    body += url
    body += "\n"
    body += "\n"
    body += "Created by TRACE"
    body += "\n"
    body += "Automatic highlights so you never have to edit again."
    body += "\n"
    body += "https://traceup.com"
    var encBody = encodeURIComponent(body)

    return `mailto:?Subject=${subject}&Body=${encBody}`
}

function copy(e, toast) {
    e.preventDefault()
    var el = document.getElementById("copy-link")
    el.contentEditable = true
    el.readOnly = true

    var range = document.createRange()
    range.selectNodeContents(el)
    var sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range) // Does not work for Firefox if a textarea or input
    if (el.nodeName === "TEXTAREA" || el.nodeName === "INPUT") {
        el.select() // Firefox will only select a form element with select()
    }
    if (
        el.setSelectionRange &&
        navigator.userAgent.match(/ipad|ipod|iphone/i)
    ) {
        el.setSelectionRange(0, 999999) // iOS only selects "form" elements with SelectionRange
    }

    try {
        document.execCommand("copy") // Security exception may be thrown by some browsers.
    } catch (ex) {
        console.warn("Copy to clipboard failed.", ex)
    }
    console.log("toast", toast)
    toast("Copied")
}

const canNativeShare = () => {
    return navigator.share
}

export const nativeShareMoment = (game, playlist, event, user) => {
    if (navigator.share) {
        let url = window.location.href
        url += "?mtm_campaign=gg"
        url += "&mtm_kwd=native"

        let date = moment(game?.date)
        let gameDate = date.format("MMMM Do, YYYY")

        let text = `${playlist?.title} playlist from match on ${gameDate}.`

        navigator
            .share({
                text,
                url,
            })
            .then(() => {
                sharedMomentToCZ({
                    type: "native share",
                    user_id: user?.user_id,
                    where: "main",
                    moment_id: event?.game_clip_id,
                })
                console.log("nativeShareMoment", "success")
            })
            .catch((err) => {
                console.log("nativeShareMoment", "failed", err)
            })
        return true
    }
    return false
}
